import {useCallback, useEffect, useRef, useState} from 'react';
import type {
  OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
  OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
} from '@stripe-internal/data-v1/schema/types';

const REFRESH_INTERVAL_S = 60;

type RequestRelayQuoteFn = (
  inputCurrency: OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
  inputChain: OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
) => Promise<unknown>;

export const useQuoteRefresh = (
  requestRelayQuote: RequestRelayQuoteFn,
  selectedToken: OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
  selectedNetwork: OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum | null,
  paused: boolean,
): {secondsUntilRefresh: number} => {
  const [secondsUntilRefresh, setSecondsUntilRefresh] =
    useState(REFRESH_INTERVAL_S);
  const countRef = useRef(REFRESH_INTERVAL_S);

  const refresh = useCallback(async () => {
    if (!selectedNetwork) return;
    try {
      await requestRelayQuote(selectedToken, selectedNetwork);
    } catch {
      // TODO: determine what to do when subsequent quote refreshes fail.
    }
  }, [requestRelayQuote, selectedToken, selectedNetwork]);

  useEffect(() => {
    if (paused) return;

    refresh();
    countRef.current = REFRESH_INTERVAL_S;
    setSecondsUntilRefresh(REFRESH_INTERVAL_S);

    const interval = setInterval(() => {
      countRef.current -= 1;
      if (countRef.current <= 0) {
        countRef.current = REFRESH_INTERVAL_S;
        refresh();
      }
      setSecondsUntilRefresh(countRef.current);
    }, 1000);

    return () => clearInterval(interval);
  }, [refresh, paused]);

  return {secondsUntilRefresh};
};
